import { useEffect, useMemo, useState } from 'react';

import type { ClassNameProps } from '@tripleten/cn';

import type { Validator } from 'src/shared/utils/validation-rules.js';

import { cnPasswordStrength } from './password-strength.constants.js';

import './password-strength-meter.css';

export interface PasswordStrengthMeterProps extends ClassNameProps {
    password: string;
    hintText: string;
    strengthLevels: {
        validators: Validator[];
        message: string;
    }[];
}

export const PasswordStrengthMeter = (props: PasswordStrengthMeterProps) => {
    const { password, className, hintText, strengthLevels } = props;

    const [strengthLevel, setStrengthLevel] = useState(0);
    const [meterTitle, setMeterTitle] = useState(strengthLevels[0]?.message ?? '');

    useEffect(() => {
        for (let level = 0; level < strengthLevels.length; level++) {
            const currentLevel = strengthLevels[level];

            if (!currentLevel) continue;

            const validators = currentLevel.validators;

            // validators.length === 0 means that it's the last level — "Password is strong enough"
            const shouldSkipLevel = validators.length !== 0 && validators.every((validator) => validator(password));

            if (!shouldSkipLevel) {
                // eslint-disable-next-line react-hooks/set-state-in-effect -- Recomputes the meter from the password on every keystroke; the early return makes it converge in one pass.
                setStrengthLevel(level + 1);
                setMeterTitle(currentLevel.message);

                return;
            }
        }
    }, [password, strengthLevels]);

    const barLevelStyle = useMemo(
        () => ({
            width: `${(100 / strengthLevels.length) * strengthLevel}%`,
        }),
        [strengthLevels, strengthLevel],
    );

    const isSuccessState = strengthLevel === strengthLevels.length;

    return (
        <div className={cnPasswordStrength({}, [className])}>
            <div className={cnPasswordStrength('meter', { success: isSuccessState })}>
                <div className={cnPasswordStrength('meter-title')}>{meterTitle}</div>
                <div className={cnPasswordStrength('meter-bar')}>
                    <div className={cnPasswordStrength('meter-bar-level')} style={barLevelStyle}></div>
                </div>
            </div>

            <div className={cnPasswordStrength('hint')}>{hintText}</div>
        </div>
    );
};
